var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useState } from 'react';
import { breakpoints } from 'Styles/breakpoints';
import { white } from 'Styles/colors';
import { useBreakpoint } from '../../util/Breakpoint.context';
import { client, generateWidth } from '../../util/cloudinary';
import * as Styled from './ImageContent.styled';
import { isBrowser } from '../../util/ssr';
var SORT_ORDER = {
    small: 4,
    medium: 3,
    large: 2,
    xlarge: 1
};
var DEFAULT_IMAGE_WIDTH = 1000;
/**
 * This image component is meant to be highly manual with creative team cutting and uploading.
 * No automated cropping ever.
 *
 * It renders an image with a placeholder.
 * It uses Cloudinary to fetch the image and apply transformations.
 */
var ImageContent = function (_a) {
    var alt = _a.alt, _b = _a.background, background = _b === void 0 ? white : _b, children = _a.children, className = _a.className, crop = _a.crop, _c = _a.cropType, cropType = _c === void 0 ? 'crop' : _c, _d = _a.fetchFormat, fetchFormat = _d === void 0 ? 'auto' : _d, height = _a.height, _e = _a.isLazy, isLazy = _e === void 0 ? true : _e, maxWidth = _a.maxWidth, onLowResLoad = _a.onLowResLoad, _f = _a.resolution, resolution = _f === void 0 ? {} : _f, src = _a.src, width = _a.width, _g = _a.withProgressiveLoading, withProgressiveLoading = _g === void 0 ? true : _g, columnSpan = _a.columnSpan, imgAttributes = __rest(_a, ["alt", "background", "children", "className", "crop", "cropType", "fetchFormat", "height", "isLazy", "maxWidth", "onLowResLoad", "resolution", "src", "width", "withProgressiveLoading", "columnSpan"]);
    var ConditionalLazyWrapper = isLazy ? Styled.LazyLoad : React.Fragment;
    var _h = useState(), devicePixelRatio = _h[0], setDevicePixelRatio = _h[1];
    var currentBreakpoints = useBreakpoint();
    var _j = useState(false), hasHighResImageLoaded = _j[0], setHighResImageLoaded = _j[1];
    var createUrl = function (srcSetWidth) {
        return client.url(src, {
            type: 'fetch',
            fetch_format: fetchFormat,
            quality: 'auto',
            transformation: [
                __assign({ crop: cropType }, crop),
                {
                    background: background,
                    width: srcSetWidth,
                    crop: 'scale'
                }
            ]
        });
    };
    var imgSrcDefault = createUrl(DEFAULT_IMAGE_WIDTH);
    var imgBreakpoints = Object.keys(resolution);
    var matchingBreakpointResolution = imgBreakpoints
        .sort(function (a, b) {
        return (SORT_ORDER[a] || 0) -
            (SORT_ORDER[b] || 0);
    })
        .find(function (breakpoint) {
        return breakpoints[breakpoint] &&
            resolution[breakpoint] !== undefined &&
            currentBreakpoints[breakpoint];
    });
    var imgWidth = matchingBreakpointResolution
        ? resolution[matchingBreakpointResolution]
        : DEFAULT_IMAGE_WIDTH;
    var urlWidth = imgWidth;
    if (columnSpan) {
        var parsedColumnSpan = parseInt(columnSpan, 10);
        urlWidth *= parsedColumnSpan;
    }
    if (devicePixelRatio && devicePixelRatio > 1) {
        urlWidth *= devicePixelRatio;
    }
    if (maxWidth) {
        urlWidth = Math.min(urlWidth, maxWidth);
    }
    /**
     * @todo: Wrapping urlWidth to ensure is a whole number. Needs to refactor for a more efficient solution.
     */
    var imgSrc = createUrl(Math.ceil(urlWidth));
    useEffect(function () {
        setDevicePixelRatio((window === null || window === void 0 ? void 0 : window.devicePixelRatio) || 1);
    }, [isBrowser()]);
    return (React.createElement(Styled.Container, { className: className, aspectRatio: (crop === null || crop === void 0 ? void 0 : crop.height) && (crop === null || crop === void 0 ? void 0 : crop.width) ? crop.height / crop.width : 1, hasLoaded: !withProgressiveLoading || hasHighResImageLoaded },
        React.createElement(ConditionalLazyWrapper, null,
            withProgressiveLoading && src && (React.createElement(Styled.Image, { src: client.url(src, {
                    type: 'fetch',
                    fetch_format: fetchFormat,
                    quality: '1',
                    transformation: [
                        __assign({ crop: cropType }, crop),
                        {
                            background: background,
                            width: crop ? generateWidth(crop) : undefined,
                            crop: 'scale'
                        }
                    ]
                }), hasLoaded: true, onLoad: onLowResLoad, alt: alt || '', role: "presentation" })),
            devicePixelRatio ? (React.createElement(Styled.Image, __assign({ src: imgSrc, alt: alt || '', onLoad: function () { return setHighResImageLoaded(true); }, hasLoaded: !withProgressiveLoading || hasHighResImageLoaded, width: width, height: height }, imgAttributes))) : (React.createElement(Styled.Image, __assign({ src: imgSrcDefault, alt: alt || '', onLoad: function () { return setHighResImageLoaded(true); }, hasLoaded: !withProgressiveLoading || hasHighResImageLoaded, width: width, height: height }, imgAttributes))),
            children)));
};
export default ImageContent;
